import type React from 'react'
import InfoBox, {
  type InfoBoxColorscheme,
} from '../../../../../../components/InfoBox'
import type { WealthManagerLifecycleStatus } from '../../../../../../redux/services/coreApi'
import type { RiskTier } from '../../../../../../types/coreApi-types'

// How each rung of the WM lifecycle ladder reads in the admin table. "Active" is the WM adjective —
// never "Activated", which is reserved for a client's platform entry (CONTEXT.md).
const STATUS_PRESENTATION: Record<
  WealthManagerLifecycleStatus,
  { label: string; colorScheme: InfoBoxColorscheme }
> = {
  Seeded: { label: 'Seeded', colorScheme: 'gray' },
  Invited: { label: 'Invited', colorScheme: 'orange' },
  Active: { label: 'Active', colorScheme: 'green' },
}

// The status chip, plus a warning chip when a WM is Active but was never Invited (handoff decision 5).
// The derived status collapses that anomaly to Active, so the warning is driven off the separate
// `isInvited` flag — it flags a real gap (an agreement attested without a login ever minted) for the
// admin to notice, without changing the permissive backend behaviour.
export const WmStatusBadge: React.FC<{
  status: WealthManagerLifecycleStatus
  isInvited: boolean
}> = ({ status, isInvited }) => {
  const { label, colorScheme } = STATUS_PRESENTATION[status]
  const showWarning = status === 'Active' && !isInvited

  return (
    <div className="flex items-center gap-2 w-min">
      <InfoBox colorScheme={colorScheme}>{label}</InfoBox>
      {showWarning && (
        <span title="Marked active without ever being invited — no Auth0 login was minted.">
          <InfoBox colorScheme="red">No invite</InfoBox>
        </span>
      )}
    </div>
  )
}

const RISK_PRESENTATION: Record<
  RiskTier,
  { label: string; colorScheme: InfoBoxColorscheme }
> = {
  Low: { label: 'Low', colorScheme: 'green' },
  Medium: { label: 'Medium', colorScheme: 'orange' },
  High: { label: 'High', colorScheme: 'red' },
}

// The admin-set WM risk rating as a chip; an unset rating reads "Unset" in neutral gray. Say "rating" for
// a WM, never "tier" (that belongs to an Investor's computed ComplianceProfile) — CONTEXT.md.
export const RiskRatingBadge: React.FC<{ riskRating?: RiskTier | null }> = ({
  riskRating,
}) => {
  const presentation = riskRating
    ? RISK_PRESENTATION[riskRating]
    : { label: 'Unset', colorScheme: 'gray' as InfoBoxColorscheme }

  return (
    <div className="w-min">
      <InfoBox colorScheme={presentation.colorScheme}>
        {presentation.label}
      </InfoBox>
    </div>
  )
}
